/**
 * 仕様書 v0.3 §5（計算仕様）および §7.2（アイテム設計）に対応する型定義。
 */

/** 席（座順）。東 = 起家。3人打ちでは 'N' を使わない（§2 用語定義） */
export type Seat = 'E' | 'S' | 'W' | 'N';

/**
 * 席順。同点時に「起家に近い方が上位」を決める唯一の根拠（§5.1 Step 1）。
 * 数値が小さいほど上位。
 */
export const SEAT_ORDER: Readonly<Record<Seat, number>> = Object.freeze({
  E: 0,
  S: 1,
  W: 2,
  N: 3,
});

/** 4人打ちで有効な席 */
export const SEATS_4P: readonly Seat[] = Object.freeze(['E', 'S', 'W', 'N'] as const);

/** 3人打ちで有効な席。北は存在しない（§5.1 Step 1） */
export const SEATS_3P: readonly Seat[] = Object.freeze(['E', 'S', 'W'] as const);

/** 端数処理の方式（§4.3 / §5.1 Step 2） */
export type Rounding = 'gosha_rokunyu' | 'shisha_gonyu' | 'ceil' | 'floor';

/** 丸めで生じたズレの吸収先（§4.3 / §5.1 Step 6） */
export type ZeroSumAdjust = 'top' | 'none';

/** 飛びの判定閾値（§4.3） */
export type BankruptcyThreshold = 'below_zero' | 'zero_or_below';

/** 飛び賞の受取先（§4.3 / §4.4.1） */
export type BankruptcyPayTo = 'buster' | 'top';

/** 飛ばした人を特定できない場合の受取先（§4.3） */
export type BankruptcyFallbackPayTo = 'top' | 'none';

/** ウマのプリセット（§4.3）。3人打ちに名前付きプリセットはない */
export type UmaPreset = 'gotto' | 'one_two' | 'one_three' | 'two_three' | 'custom';

export interface BankruptcyRule {
  /** 飛び賞そのものの有無 */
  enabled: boolean;
  /** 飛んだ人が支払うポイント */
  point: number;
  /** below_zero: 0点未満で飛び / zero_or_below: 0点以下で飛び */
  threshold: BankruptcyThreshold;
  /** buster: 飛ばした人 / top: トップ */
  payTo: BankruptcyPayTo;
  /** payTo=buster で飛ばした人を特定できないときの受取先 */
  fallbackPayTo: BankruptcyFallbackPayTo;
}

/**
 * 計算に必要なルール。対局レコードには「その時点のルール」をスナップショットとして
 * 丸ごと保存するため（§4.3 重要な設計方針）、この型がそのまま保存される。
 */
export interface Rule {
  playerCount: 3 | 4;
  /** 配給原点。対局開始時の持ち点 */
  startPoints: number;
  /** 返し点。精算時に素点から差し引く基準点 */
  returnPoints: number;
  /** 順位順のウマ。要素数は playerCount と一致し、合計は 0 であること */
  uma: number[];
  rounding: Rounding;
  zeroSumAdjust: ZeroSumAdjust;
  bankruptcy: BankruptcyRule;
}

/** ルールセット。計算には不要だがグループ設定として保持する項目を含む（§4.3） */
export interface RuleSet extends Rule {
  ruleId: string;
  name: string;
  umaPreset: UmaPreset;
  /** 飛び発生時に終了とみなすか。入力バリデーション用で計算には影響しない */
  endOnBankruptcy: boolean;
  isDefault?: boolean;
}

/** 計算の入力。1人分の対局結果（§8.1 リクエスト例） */
export interface ResultInput {
  memberId: string;
  /** 表示名のスナップショット。改名しても過去の対局表示が変わらないようにする（§7.2） */
  nameSnapshot?: string;
  /** 任意入力。同点者がいる場合のみ必須になる（§4.4.3） */
  seat?: Seat | null;
  rawScore: number;
  /** 飛んだ人にのみ設定。「該当なし（流局等）」は null（§4.4.1） */
  bustedBy?: string | null;
}

/** 計算の出力。入力に順位と各ポイントを付与したもの（§5.1） */
export interface ResultOutput extends ResultInput {
  rank: number;
  isBankrupt: boolean;
  /** 素点ポイント。(素点 − 返し点) を丸めたもの */
  scorePoint: number;
  umaPoint: number;
  okaPoint: number;
  /** 飛び賞の授受。支払いは負、受け取りは正。全員の合計は常に 0 */
  bankruptcyPoint: number;
  /** 精算ポイント。scorePoint + umaPoint + okaPoint + bankruptcyPoint */
  totalPoint: number;
  /** 同点で、席順により下位に置かれた行。詳細画面のラベル表示に使う（§5.1 Step 1） */
  tiedButLower: boolean;
}

/** 飛び賞の授受1件。対局詳細で「D → B に10pt」と表示するための情報（§9.2） */
export interface BankruptcyTransfer {
  fromMemberId: string;
  /** 受取人。fallbackPayTo=none で授受が発生しなかった場合は null */
  toMemberId: string | null;
  point: number;
}

export interface MatchCalculation {
  /** 順位の昇順。§7.2 の Match アイテムに保存される並び */
  results: ResultOutput[];
  /** 飛び賞の授受の内訳 */
  transfers: BankruptcyTransfer[];
  /**
   * 丸めのズレを1位に寄せた量。0 以外なら対局詳細に注記する（§5.1 Step 6）。
   * zeroSumAdjust=none の場合は補正しないため、合計が 0 にならないことがある。
   */
  zeroSumAdjustment: number;
  /** オカ。(返し点 − 配給原点) × 人数 / 1000 */
  oka: number;
}
